import { createContext, use, useCallback, useEffect, useState, type ReactNode } from 'react'
import { api, ApiError, setUnauthorizedHandler, type Me } from '../api/client'

/**
 * Состояние аутентификации административного приложения.
 *
 * При загрузке страницы выполняется запрос текущей сессии: маркер лежит
 * в куке, недоступной коду, поэтому единственный способ узнать, выполнен
 * ли вход, — спросить серверную часть.
 */

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; me: Me }

type AuthContextValue = {
  state: AuthState
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  /** Сессия закончилась во время работы, а не «ещё не входили». */
  expired: boolean
  switchCompany: (companyId: number | null) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })
  const [expired, setExpired] = useState(false)

  // Сессия живёт своим сроком и заканчивается посреди работы. Любой
  // ответ «требуется вход» переводит приложение на вход — иначе экран
  // показывал бы ошибку, из которой пользователю ничего не сделать.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setState((now) => {
        // Пометка о сроке ставится только тем, кто был внутри: для
        // не входившего это не «сессия истекла», а обычное начало работы.
        if (now.status === 'authenticated') setExpired(true)
        return { status: 'anonymous' }
      })
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  useEffect(() => {
    let cancelled = false
    api
      .me()
      .then((me) => {
        if (!cancelled) setState({ status: 'authenticated', me })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        // Отсутствие сессии — обычное состояние, а не ошибка.
        if (e instanceof ApiError && e.unauthorized) {
          setState({ status: 'anonymous' })
        } else {
          setState({ status: 'anonymous' })
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const me = await api.login(email, password)
    setExpired(false)
    setState({ status: 'authenticated', me })
  }, [])

  const logout = useCallback(async () => {
    try {
      setExpired(false)
      await api.logout()
    } finally {
      setState({ status: 'anonymous' })
    }
  }, [])

  const switchCompany = useCallback(async (companyId: number | null) => {
    const me = await api.switchCompany(companyId)
    setState({ status: 'authenticated', me })
  }, [])

  return (
    <AuthContext value={{ state, login, logout, switchCompany, expired }}>{children}</AuthContext>
  )
}

export function useAuth() {
  const ctx = use(AuthContext)
  if (!ctx) throw new Error('useAuth применён вне AuthProvider')
  return ctx
}
